import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { formatDobNumeric } from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { RequestDetail, RequestDetailPatient } from '../../models/request-detail';
import { PatientRecordsSearchStateService } from '../../services/patient-records-search-state.service';
import { PatientPortalUserRequestsService } from '../../services/patient-portal-user-requests.service';
import {
  MAX_SEARCH_MRN_LENGTH,
  MAX_SEARCH_NAME_LENGTH,
  MAX_SEARCH_SSN_LENGTH,
  MIN_SEARCH_FIELD_LENGTH,
  PatientSearchCriteria,
  buildPatientSearchRequest,
  hasEnoughCriteria,
  isSearchDateOfBirthInvalid,
  isSearchFieldTooShort,
  isSearchSsnInvalid,
} from '../../utils/patient-search.utils';
import {
  NOT_AVAILABLE,
  formatPatientAddress,
  formatPatientMrn,
  formatPatientPcp,
  formatPatientPhone,
  getPatientInitials,
  splitDisplayName,
} from '../../../shared/utils/patient-display/patient-display.utils';
import {
  MatchConfidence,
  SubmittedIdentity,
  getLeadingMatchPatientId,
  getMatchConfidence,
  getMatchConfidenceBadge,
} from '../../utils/match-confidence.utils';

/** Form fields that can arrive pre-filled from the submitted request. */
type PrefillField = 'firstName' | 'lastName' | 'dateOfBirth' | 'ssn' | 'mrn';

/**
 * Dedicated Search Patient Records screen (Access Management - 11), reached
 * from the Request Detail header. The form arrives pre-filled from the
 * submitted request; staff can edit any field and re-run the search, then
 * Select & Review a record to carry it back to the Request Detail screen.
 */
@Component({
  selector: 'app-search-patient-records',
  standalone: true,
  imports: [RouterLink, IconComponent, ProgressSpinnerComponent],
  templateUrl: './search-patient-records.html',
  styleUrl: './search-patient-records.scss',
})
export class SearchPatientRecords implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly requestsService = inject(PatientPortalUserRequestsService);
  private readonly searchStateService = inject(PatientRecordsSearchStateService);

  readonly requestId = Number(this.route.snapshot.paramMap.get('requestId') ?? 0);

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly request = signal<RequestDetail | null>(null);
  readonly results = signal<RequestDetailPatient[]>([]);
  /**
   * Server-side total for the search behind `results`. `null` means unknown —
   * the stored auto-search arrives with the request detail, which carries no
   * total — and suppresses the truncation warning rather than guessing.
   */
  readonly resultsTotalCount = signal<number | null>(null);
  readonly isSearching = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly hasSearched = signal(false);

  readonly lastNameValue = signal('');
  readonly firstNameValue = signal('');
  readonly dateOfBirthValue = signal('');
  readonly ssnValue = signal('');
  readonly mrnValue = signal('');

  // Snapshot of the values seeded from the request, so the template can keep
  // the "pre-filled" tint only while a field still holds its submitted value.
  // A signal (not a plain field) — this app is zoneless, so only signal
  // writes schedule change detection.
  private readonly prefilled = signal<Record<PrefillField, string>>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    mrn: '',
  });

  readonly patientName = computed(() => this.request()?.patientDisplayName ?? '');
  /**
   * Text for the breadcrumb link back to the Request Detail screen. A request
   * with no submitted name would otherwise render an anchor with no text and no
   * accessible name (WCAG 2.4.4 / 4.1.2) — an invisible, unclickable back-route.
   * The request id names the destination without asserting a name that was never
   * submitted.
   */
  readonly breadcrumbLabel = computed(() => this.patientName() || `Request #${this.requestId}`);
  readonly loadErrorTitle = computed(() =>
    this.notFound() ? 'Request not found' : 'Unable to load request',
  );
  readonly detailLink = computed(() => ['/request-detail', this.requestId]);
  // A DOB the user typed that isn't a real MM/DD/YYYY date blocks the search
  // (with an inline error) rather than silently sending garbage to the API.
  // The rule is shared with the detail screen's Adjust Search panel.
  readonly dateOfBirthInvalid = computed(() => isSearchDateOfBirthInvalid(this.dateOfBirthValue()));
  // A populated free-text criterion below the API's minimum, or an SSN with a
  // non-digit in it, blocks the search with an inline error for the same reason
  // as a malformed date: the API rejects it, and dropping the field instead
  // would silently widen a PHI search past what was asked for.
  readonly firstNameInvalid = computed(() => isSearchFieldTooShort(this.firstNameValue()));
  readonly lastNameInvalid = computed(() => isSearchFieldTooShort(this.lastNameValue()));
  readonly mrnInvalid = computed(() => isSearchFieldTooShort(this.mrnValue()));
  readonly ssnInvalid = computed(() => isSearchSsnInvalid(this.ssnValue()));

  readonly minFieldLength = MIN_SEARCH_FIELD_LENGTH;
  readonly maxNameLength = MAX_SEARCH_NAME_LENGTH;
  readonly maxMrnLength = MAX_SEARCH_MRN_LENGTH;
  readonly maxSsnLength = MAX_SEARCH_SSN_LENGTH;

  readonly canRunSearch = computed(
    () =>
      !this.dateOfBirthInvalid() &&
      !this.firstNameInvalid() &&
      !this.lastNameInvalid() &&
      !this.mrnInvalid() &&
      !this.ssnInvalid() &&
      hasEnoughCriteria(this.buildCurrentCriteria()),
  );
  /**
   * True when the server matched more records than this page holds. The search
   * requests a fixed PAGE_SIZE with no paging UI, so without this the screen
   * would report a truncated page as the complete match set — and the
   * documented next step for "no match" here is denying the request.
   */
  readonly isTruncated = computed(() => {
    const total = this.resultsTotalCount();
    return total !== null && total > this.results().length;
  });
  readonly resultSummary = computed(() => {
    const count = this.results().length;
    if (this.isTruncated()) {
      return `Showing ${count} of ${this.resultsTotalCount()}`;
    }
    return `${count} record${count === 1 ? '' : 's'} found`;
  });
  readonly emptyStateMessage = computed(() =>
    this.hasSearched()
      ? 'No patient records matched the search criteria. Adjust the fields above and try again, or return to the request to deny it if identity cannot be verified.'
      : 'No patient records matched the submitted details. Adjust the fields above and run a search.',
  );

  /**
   * What every result is scored against: the identity as it was **submitted**, never the search
   * boxes above.
   *
   * The two come apart the moment a staff member edits a field — which is the point of this screen
   * — and scoring against the edited criteria would make confidence self-fulfilling: search for the
   * name on the record and every row comes back a confident match for a request that said something
   * else entirely.
   */
  private readonly submittedIdentity = computed<SubmittedIdentity | null>(() => {
    const request = this.request();
    return request
      ? {
          patientDisplayName: request.patientDisplayName,
          dateOfBirth: request.dateOfBirth,
          medicalRecordNumber: request.medicalRecordNumber,
        }
      : null;
  });

  /**
   * Each result's confidence, scored once per results change rather than per row on every change
   * detection. Feeds both the row chips and {@link leadingMatchPatientId}.
   */
  private readonly resultConfidences = computed(() => {
    const submitted = this.submittedIdentity();
    return new Map<number, MatchConfidence>(
      submitted
        ? this.results().map((patient) => [
            patient.patientId,
            getMatchConfidence(submitted, patient),
          ])
        : [],
    );
  });

  /**
   * The one row to draw the eye to, or null when no single row leads. See
   * {@link getLeadingMatchPatientId} — a highlight on one of several equally-good matches reads as
   * an endorsement the data can't support.
   */
  readonly leadingMatchPatientId = computed(() =>
    getLeadingMatchPatientId(this.resultConfidences()),
  );

  /**
   * The leader is worked out over the rows on screen, so when the search found more than it shows
   * (or the total is unknown) the label says so rather than overstating it.
   */
  readonly leadingMatchLabel = computed(() => {
    const total = this.resultsTotalCount();
    return total !== null && total <= this.results().length
      ? 'Closest match'
      : 'Closest on this page';
  });

  ngOnInit(): void {
    if (!Number.isInteger(this.requestId) || this.requestId <= 0) {
      this.isLoading.set(false);
      this.notFound.set(true);
      this.loadError.set('This request may have been removed, or the link is no longer valid.');
      return;
    }

    this.requestsService
      .getRequestById(this.requestId)
      .pipe(
        catchError((error) => {
          logHttpError('Failed to load request for patient record search', error);
          const notFound = error?.status === 404;
          this.notFound.set(notFound);
          this.loadError.set(
            notFound
              ? 'This request may have been removed, or the link is no longer valid.'
              : 'Something went wrong while loading this request. Please try again.',
          );
          return of(null);
        }),
        // Single owner for the spinner, so a throw while mapping the response
        // can't strand the screen on it (a null patientDisplayName used to).
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((request) => {
        if (!request) {
          return;
        }

        try {
          this.request.set(request);
          // The stored auto-search results arrive with the request detail, which
          // exposes no total — leave the count unknown rather than implying the
          // list is complete.
          this.results.set(request.patients);
          this.resultsTotalCount.set(null);
          this.prefillFromRequest(request);
        } catch (error) {
          // `finalize` un-sticks the spinner on a throw in here, but on its own
          // it would leave the screen showing a half-applied response with no
          // error — silently wrong criteria to search PHI on. Fail visibly and
          // discard the partial state instead.
          logHttpError('Failed to apply the loaded request to the search form', error);
          this.request.set(null);
          this.results.set([]);
          this.resultsTotalCount.set(null);
          this.loadError.set('Something went wrong while loading this request. Please try again.');
        }
      });
  }

  isPrefilled(field: PrefillField, value: string): boolean {
    return value.trim() !== '' && this.prefilled()[field] === value;
  }

  // Native form submit (Enter in any field, or the Search button).
  onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.runSearch();
  }

  clearForm(): void {
    this.lastNameValue.set('');
    this.firstNameValue.set('');
    this.dateOfBirthValue.set('');
    this.ssnValue.set('');
    this.mrnValue.set('');
    // A stale "Unable to search patient records" under an empty form reads as a
    // fresh failure the staff member didn't cause.
    this.searchError.set(null);
    // The results table is deliberately left standing: Clear resets the
    // criteria, and wiping the records a staff member is still reading would
    // look like the search came back empty — the state that leads to denying a
    // request. `hasSearched` stays set for the same reason, so the empty-state
    // copy keeps describing the search that actually ran.
  }

  runSearch(): void {
    if (!this.canRunSearch() || this.isSearching()) {
      return;
    }

    // The search is audited against the admin user the API reads off the access
    // token, so there is no id for this screen to supply or check.
    const searchRequest = buildPatientSearchRequest(this.requestId, this.buildCurrentCriteria());

    this.isSearching.set(true);
    this.searchError.set(null);

    this.requestsService
      .searchPatients(searchRequest)
      .pipe(
        finalize(() => {
          this.isSearching.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.results.set(result.patients);
          this.resultsTotalCount.set(result.totalRecordCount);
          this.hasSearched.set(true);
        },
        error: (error) => {
          logHttpError('Failed to search patient records', error);
          this.searchError.set('Unable to search patient records. Please try again.');
        },
      });
  }

  /**
   * Carries the chosen record back to the Request Detail screen and opens the review there.
   *
   * The confidence level isn't sent with it. The modal decides which state to open in from the same
   * predicates this screen scores with ({@link getMatchConfidence} and the identity-match utils), so
   * passing a level would hand it a second opinion that could disagree with its own override gate —
   * on the question of whether linking this record needs a documented justification.
   */
  selectAndReview(patient: RequestDetailPatient): void {
    this.searchStateService.setHandoff({
      patientPortalUserRequestId: this.requestId,
      patients: this.results(),
      selectedPatientId: patient.patientId,
      totalRecordCount: this.resultsTotalCount(),
      openReview: true,
    });
    this.router.navigate(this.detailLink());
  }

  getResultInitials(patient: RequestDetailPatient): string {
    return getPatientInitials(patient.displayName);
  }

  /**
   * `formatDobNumeric` returns an empty string for anything it can't parse, and a blank PHI cell
   * reads as a rendering glitch rather than absent data — the same fallback the address and phone
   * columns below already apply.
   */
  getResultDateOfBirth(patient: RequestDetailPatient): string {
    return formatDobNumeric(patient.dateOfBirth) || NOT_AVAILABLE;
  }

  getResultAddress(patient: RequestDetailPatient): string {
    return formatPatientAddress(patient.address);
  }

  getResultPhoneNumber(patient: RequestDetailPatient): string {
    return formatPatientPhone(patient.phoneNumber);
  }

  getResultMedicalRecordNumber(patient: RequestDetailPatient): string {
    return formatPatientMrn(patient.medicalRecordNumber);
  }

  getResultPrimaryCareProvider(patient: RequestDetailPatient): string {
    return formatPatientPcp(patient.primaryCareProvider);
  }

  /**
   * How far this record agrees with the submitted request.
   *
   * Defaults to `Low` while the request hasn't loaded, rather than to the reassuring answer: the
   * chip must never read "High" on a comparison the screen has not actually made. In practice the
   * table isn't rendered until the request is in hand.
   */
  getResultConfidence(patient: RequestDetailPatient): MatchConfidence {
    return this.resultConfidences().get(patient.patientId) ?? 'Low';
  }

  getResultConfidenceBadge(patient: RequestDetailPatient): { label: string; class: string } {
    return getMatchConfidenceBadge(this.getResultConfidence(patient));
  }

  isLeadingMatch(patient: RequestDetailPatient): boolean {
    return this.leadingMatchPatientId() === patient.patientId;
  }

  private buildCurrentCriteria(): PatientSearchCriteria {
    return {
      firstName: this.firstNameValue().trim() || null,
      lastName: this.lastNameValue().trim() || null,
      dateOfBirth: this.dateOfBirthValue().trim() || null,
      lastFiveSsn: this.ssnValue().trim() || null,
      mrn: this.mrnValue().trim() || null,
    };
  }

  private prefillFromRequest(request: RequestDetail): void {
    const nameParts = splitDisplayName(request.patientDisplayName);
    const values: Record<PrefillField, string> = {
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      dateOfBirth: formatDobNumeric(request.dateOfBirth),
      ssn: request.lastFiveSocialSecurityNumber ?? '',
      mrn: request.medicalRecordNumber ?? '',
    };

    this.firstNameValue.set(values.firstName);
    this.lastNameValue.set(values.lastName);
    this.dateOfBirthValue.set(values.dateOfBirth);
    this.ssnValue.set(values.ssn);
    this.mrnValue.set(values.mrn);
    this.prefilled.set(values);
  }
}
